import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { confirmAlert } from 'react-confirm-alert';
import {
  addCampaign,
  addGame,
  getAvailableGames,
  getGameDetail,
  resetGameScores,
  updateCampaign,
  updateGame,
  uplaodMedia,
} from 'app/api/game';
import Message from 'app/utils/Message';
import { dataformatChangesYearToDate } from 'app/utils/Parsers';
import Toast from 'app/utils/Toast';

const useCreateGame = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pageLoading, setPageLoading] = useState(false);
  const [error, setErrors] = useState({});
  const [availableGames, setAvailableGames] = useState([]);
  const [availableGamesFetchFailed, setAvailableGamesFetchFailed] =
    useState(false);
  const [values, setValues] = useState({
    title: '',
    slug: '',
    thumbnail: null,
    sortOrder: 0,
    config: '',
    isEnabled: true,
    startDate: null,
    endDate: null,
  });

  const gameId = location?.state?.id ? location?.state?.id : '';

  useEffect(() => {
    if (!gameId) {
      loadAvailableGames();
    }
    loadGameData(gameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailableGames = async () => {
    let response = await getAvailableGames();
    if (response?.responseCode === 200) {
      const unregistered = (response?.data?.games || []).filter(
        (g) => !g.registered
      );
      setAvailableGames(unregistered);
      setAvailableGamesFetchFailed(false);
    } else {
      setAvailableGamesFetchFailed(true);
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : 'Could not load available games from CDN'
      );
    }
  };

  const handleSelectGame = (slug) => {
    const selected = availableGames.find((g) => g.slug === slug);
    if (!selected) {
      return;
    }
    setValues((prevValues) => ({
      ...prevValues,
      slug: selected.slug,
      title: selected.title,
    }));
  };

  const loadGameData = async (id) => {
    if (!id) {
      return;
    }

    setPageLoading(true);
    let response = await getGameDetail(id);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      let responsedata = response.data;

      setValues((prevValues) => ({
        ...prevValues,
        ...responsedata,
        config: responsedata?.config
          ? JSON.stringify(responsedata.config, null, 2)
          : '',
        startDate: responsedata?.activeCampaign?.startDate
          ? dataformatChangesYearToDate(responsedata.activeCampaign.startDate)
          : responsedata?.startDate
          ? dataformatChangesYearToDate(responsedata.startDate)
          : null,
        endDate: responsedata?.activeCampaign?.endDate
          ? dataformatChangesYearToDate(responsedata.activeCampaign.endDate)
          : responsedata?.endDate
          ? dataformatChangesYearToDate(responsedata.endDate)
          : null,
      }));
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  const callAddOrUpdateAPI = async (parsedConfig) => {
    setPageLoading(true);

    let senddata = {
      title: values.title,
      slug: values.slug,
      thumbnail: values.thumbnail,
      sortOrder: Number(values.sortOrder) || 0,
      isEnabled: values.isEnabled,
      config: parsedConfig,
    };

    let response = {};
    let newId = gameId;

    if (gameId) {
      response = await updateGame(gameId, senddata);
    } else {
      response = await addGame(senddata);
      newId = response?.data?.id;
    }

    if (response?.responseCode === 200) {
      // v1 simplification: on create, if a campaign window was provided,
      // create a campaign for the new game. On edit, if a window was
      // provided, attempt to update the active campaign if one exists,
      // otherwise create one. Complex reconciliation across multiple
      // campaigns is out of scope for v1 (documented in SUMMARY).
      if (values.startDate && values.endDate) {
        const campaignPayload = {
          gameId: newId,
          startDate: values.startDate,
          endDate: values.endDate,
        };
        if (gameId && values?.activeCampaign?.id) {
          await updateCampaign(values.activeCampaign.id, campaignPayload);
        } else {
          await addCampaign(campaignPayload);
        }
      }

      setPageLoading(false);
      Toast.success(
        gameId ? 'Game updated successfully' : 'Game created successfully'
      );
      navigate(-1);
    } else {
      setPageLoading(false);
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  const handleCreateGame = (e) => {
    e.preventDefault();

    const validationErrors = {};
    if (!values.title) validationErrors.title = 'Game Title is required';
    if (!values.slug) validationErrors.slug = 'Slug is required';

    let parsedConfig;
    if (values.config && values.config.trim() !== '') {
      try {
        parsedConfig = JSON.parse(values.config);
      } catch (err) {
        validationErrors.config = 'Config must be valid JSON';
      }
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      callAddOrUpdateAPI(parsedConfig);
    }
  };

  const handleChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));

      if (error[name]) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: '',
        }));
      }
    }
  };

  const setChange = (name, val) => {
    setValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const callResetScoresAPI = async () => {
    setPageLoading(true);
    let response = await resetGameScores(gameId);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success('Scores reset');
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  const handleResetScores = () => {
    confirmAlert({
      title: '',
      message: `Are you sure? This permanently deletes ALL plays and scores for "${values.title}" (all campaigns). The leaderboard resets to empty.`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            callResetScoresAPI();
          },
        },
        {
          label: 'No',
        },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  };

  const uploadMediaHandler = async (file, name) => {
    if (!file) {
      setChange(name, null);
      return;
    }

    const response = await uplaodMedia(file);
    if (response.responseCode === 200) {
      Toast.success('Image upload Successful');

      const data = response.data;
      setChange(name, data?.url);
    } else {
      Toast.error('Image upload failed');
    }
  };

  return {
    handleChange,
    values,
    error,
    setValues,
    handleCreateGame,
    pageLoading,
    gameId,
    uploadMediaHandler,
    navigate,
    setChange,
    handleResetScores,
    availableGames,
    availableGamesFetchFailed,
    handleSelectGame,
  };
};

export default useCreateGame;
